import { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navigation from "@/components/Navigation";
import { ArrowLeft, Plus, Send, Edit2, Trash2, User, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactListPage({ user, onLogout }) {
  const { address, isConnected } = useAccount();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    nickname: '',
    identifier: '',
    notes: ''
  });

  useEffect(() => {
    if (user) {
      fetchContacts();
    }
  }, [user]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('routeflow_token');
      const response = await axios.get(`${API}/contacts/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setContacts(response.data);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    
    if (!formData.nickname || !formData.identifier) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('routeflow_token');
      await axios.post(`${API}/contacts`, {
        user_id: user.id,
        ...formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Contact "${formData.nickname}" added!`);
      setShowAddModal(false);
      setFormData({ nickname: '', identifier: '', notes: '' });
      fetchContacts();
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error('Failed to add contact');
    }
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    
    if (!formData.nickname || !formData.identifier) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('routeflow_token');
      await axios.put(`${API}/contacts/${editingContact.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Contact updated!');
      setEditingContact(null);
      setFormData({ nickname: '', identifier: '', notes: '' });
      fetchContacts();
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    }
  };

  const handleDeleteContact = async (contactId, nickname) => {
    if (!window.confirm(`Delete "${nickname}" from your contacts?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('routeflow_token');
      await axios.delete(`${API}/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Contact deleted');
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const handleSendTo = (contact) => {
    localStorage.setItem('send_to_contact', JSON.stringify(contact));
    window.location.href = '/crypto/send';
  };

  const startEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      nickname: contact.nickname,
      identifier: contact.identifier,
      notes: contact.notes || ''
    });
  };

  const filteredContacts = contacts.filter(contact =>
    contact.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.identifier.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-md mx-auto border-2 border-emerald-200">
            <CardHeader>
              <CardTitle className="text-center">Connect Your Wallet</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-4">
                Please connect your wallet to manage contacts
              </p>
              <Button onClick={() => window.location.href = '/crypto/wallet'}>
                Go to Wallet
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => window.location.href = '/crypto/wallet'}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                My Contacts
              </h1>
              <p className="text-gray-600 mt-1">
                Save frequent recipients for quick transfers
              </p>
            </div>
          </div>
          
          <Button
            onClick={() => {
              setShowAddModal(true);
              setFormData({ nickname: '', identifier: '', notes: '' });
            }}
            className="bg-gradient-to-r from-emerald-500 to-teal-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Contact
          </Button>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Search */}
          <Card className="border-2 border-emerald-200">
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search contacts by name or @username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact List */}
          {loading ? (
            <Card className="border-emerald-200">
              <CardContent className="py-12 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading contacts...</p>
              </CardContent>
            </Card>
          ) : filteredContacts.length === 0 ? (
            <Card className="border-emerald-200">
              <CardContent className="py-12 text-center">
                <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {searchTerm ? 'No contacts found' : 'No contacts yet'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm 
                    ? 'Try adjusting your search' 
                    : 'Add your first contact to make transfers easier'}
                </p>
                {!searchTerm && (
                  <Button
                    onClick={() => setShowAddModal(true)}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Contact
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredContacts.map((contact) => (
                <Card key={contact.id} className="border-emerald-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        {/* Avatar */}
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {contact.nickname.charAt(0).toUpperCase()}
                        </div>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-lg">
                            {contact.nickname}
                          </h3>
                          <p className="text-sm text-gray-600 flex items-center gap-2">
                            {contact.identifier.startsWith('@') && (
                              <User className="w-3 h-3 text-emerald-600" />
                            )}
                            {contact.identifier}
                          </p>
                          {contact.notes && (
                            <p className="text-xs text-gray-500 mt-1 italic">
                              {contact.notes}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleSendTo(contact)}
                          size="sm"
                          className="bg-gradient-to-r from-emerald-500 to-teal-500"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Send
                        </Button>
                        <Button
                          onClick={() => startEdit(contact)}
                          size="sm"
                          variant="outline"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteContact(contact.id, contact.nickname)}
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Contact Modal */}
      {(showAddModal || editingContact) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md border-2 border-emerald-200">
            <CardHeader>
              <CardTitle>
                {editingContact ? 'Edit Contact' : 'Add New Contact'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={editingContact ? handleUpdateContact : handleAddContact} className="space-y-4">
                <div>
                  <Label htmlFor="nickname">Nickname *</Label>
                  <Input
                    id="nickname"
                    placeholder="Mom, John, Coffee Shop..."
                    value={formData.nickname}
                    onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                    required
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    A friendly name to remember this contact
                  </p>
                </div>

                <div>
                  <Label htmlFor="identifier">@Username or Wallet Address *</Label>
                  <Input
                    id="identifier"
                    placeholder="@john or 0x..."
                    value={formData.identifier}
                    onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                    required
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Where to send money to this contact
                  </p>
                </div>

                <div>
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Input
                    id="notes"
                    placeholder="Monthly rent, Birthday gift..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Add a note to remember why you added this contact
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingContact(null);
                      setFormData({ nickname: '', identifier: '', notes: '' });
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500"
                  >
                    {editingContact ? 'Update' : 'Add Contact'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
